import React, { useState, useEffect } from "react";
import Icon from "components/AppIcon";
import api from "../../../lib/api";
import { useAuth } from "../../../contexts/AuthContext";
import { useSubscriptionLimits } from "../../../hooks/useSubscriptionLimits";
import PlanLimitBanner from "../../../components/PlanLimitBanner";

const emptyForm = { name: "", address: "", phone: "", capacity: "" };

export default function BranchSetup({ onBranchChange }) {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editBranch, setEditBranch] = useState(null); // branch object being edited
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [isLimitError, setIsLimitError] = useState(false);
  const [memberCounts, setMemberCounts] = useState({});
  const [maxBranches, setMaxBranches] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [deleteError, setDeleteError] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const { checkLimit, refetch: refetchLimits } = useSubscriptionLimits(user?.id);

  const fetchBranches = async () => {
    try {
      setLoading(true);
      const [branchesRes, membersRes, subRes] = await Promise.allSettled([
        api.get('/branches'),
        api.get('/members', { params: { limit: 2000 } }),
        api.get('/super-admin/my-subscription'),
      ]);

      const data = branchesRes?.status === 'fulfilled' ? (branchesRes.value?.data || []) : [];
      setBranches(data);

      const counts = {};
      if (membersRes?.status === 'fulfilled') {
        (membersRes.value?.data || []).forEach(m => {
          if (m?.branch_id) counts[m.branch_id] = (counts[m.branch_id] || 0) + 1;
        });
      }
      setMemberCounts(counts);

      if (subRes?.status === 'fulfilled') {
        const mb = subRes.value?.data?.max_branches;
        setMaxBranches(mb !== undefined ? mb : null);
      }
    } catch (err) {
      console.error('Error fetching branches:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBranches();
  }, []);

  const handleChange = e => setForm(prev => ({ ...prev, [e?.target?.name]: e?.target?.value }));

  const openCreate = () => {
    setEditBranch(null);
    setForm(emptyForm);
    setError(null);
    setIsLimitError(false);
    setShowForm(true);
  };

  const openEdit = branch => {
    setEditBranch(branch);
    setForm({
      name: branch?.name || "",
      address: branch?.address || "",
      phone: branch?.phone || "",
      capacity: branch?.capacity != null ? String(branch.capacity) : "",
    });
    setError(null);
    setIsLimitError(false);
    setShowForm(true);
    setConfirmDeleteId(null);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditBranch(null);
    setForm(emptyForm);
    setError(null);
    setIsLimitError(false);
  };

  const handleSave = async () => {
    if (!form?.name?.trim()) return;

    if (!editBranch) {
      const limitMsg = checkLimit("branches");
      if (limitMsg) { setError(limitMsg); setIsLimitError(true); return; }
    }
    setIsLimitError(false);

    try {
      setSaving(true);
      setError(null);
      const payload = {
        name: form?.name,
        address: form?.address || null,
        phone: form?.phone || null,
        capacity: form?.capacity ? parseInt(form?.capacity) : 100,
      };

      if (editBranch) {
        await api.put(`/branches/${editBranch.id}`, payload);
      } else {
        await api.post('/branches', payload);
      }

      closeForm();
      await fetchBranches();
      refetchLimits();
      onBranchChange?.();
    } catch (err) {
      console.error('Error saving branch:', err);
      const apiMessage = err?.response?.data?.message || err?.message;
      const is403 = err?.response?.status === 403;
      setError(apiMessage);
      setIsLimitError(is403);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      setDeleting(true);
      setDeleteError(null);
      await api.delete(`/branches/${id}`);
      setConfirmDeleteId(null);
      await fetchBranches();
      refetchLimits();
      onBranchChange?.();
    } catch (err) {
      console.error('Error deleting branch:', err);
      setDeleteError(err?.response?.data?.message || err?.message);
    } finally {
      setDeleting(false);
    }
  };

  const canAddMore = maxBranches === null || maxBranches === -1 || branches.length < maxBranches;

  return (
    <div className="bg-[var(--color-card)] border border-[var(--color-border)] rounded-[var(--radius-md)] shadow-[var(--shadow-sm)] overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
        <div>
          <h3 className="text-sm font-semibold font-[var(--font-heading)] text-[var(--color-foreground)]">Branch Management</h3>
          <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)] mt-0.5">
            {loading ? 'Loading...' : `${branches?.length} branches configured`}
          </p>
        </div>
        {canAddMore && (
          <button onClick={showForm && !editBranch ? closeForm : openCreate} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors">
            <Icon name={showForm && !editBranch ? "X" : "Plus"} size={13} color="white" />
            {showForm && !editBranch ? "Cancel" : "Add Branch"}
          </button>
        )}
      </div>

      {showForm && (
        <div className="p-4 bg-[var(--color-muted)]/30 border-b border-[var(--color-border)]">
          <p className="text-xs font-semibold font-[var(--font-caption)] text-[var(--color-foreground)] mb-3">
            {editBranch ? `Edit Branch — ${editBranch.name}` : "New Branch Setup"}
          </p>
          {error && isLimitError ? (
            <PlanLimitBanner message={error} />
          ) : error ? (
            <p className="text-xs text-[var(--color-error)] mb-2 font-[var(--font-caption)]">{error}</p>
          ) : null}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[["name","Branch Name","text"],["address","Address","text"],["phone","Phone","tel"],["capacity","Max Capacity","number"]]?.map(([name, label, type]) => (
              <div key={name}>
                <label className="block text-xs font-medium font-[var(--font-caption)] text-[var(--color-muted-foreground)] mb-1">{label}</label>
                <input type={type} name={name} value={form?.[name]} onChange={handleChange} placeholder={label} className="w-full px-3 py-2 text-sm border border-[var(--color-border)] rounded-md bg-[var(--color-input)] text-[var(--color-foreground)] placeholder:text-[var(--color-muted-foreground)] focus:outline-none focus:ring-2 focus:ring-[var(--color-ring)]" />
              </div>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button onClick={closeForm} className="px-3 py-1.5 text-xs font-medium border border-[var(--color-border)] rounded-md text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors">Cancel</button>
            <button onClick={handleSave} disabled={saving} className="px-3 py-1.5 text-xs font-medium bg-[var(--color-primary)] text-white rounded-md hover:bg-[var(--color-secondary)] transition-colors disabled:opacity-60">
              {saving ? (editBranch ? 'Updating...' : 'Saving...') : (editBranch ? 'Update Branch' : 'Save Branch')}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-6 flex items-center justify-center">
          <Icon name="Loader2" size={16} color="var(--color-muted-foreground)" className="animate-spin" />
        </div>
      ) : (
        <div className="divide-y divide-[var(--color-border)]">
          {branches?.length === 0 ? (
            <div className="p-6 text-center text-sm text-[var(--color-muted-foreground)] font-[var(--font-caption)]">
              No branches yet. Add your first branch.
            </div>
          ) : (
            branches?.map(branch => (
              <div key={branch?.id} className="p-4 hover:bg-[var(--color-muted)]/30 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-md bg-[var(--color-primary)]/10 flex items-center justify-center flex-shrink-0">
                      <Icon name="MapPin" size={16} color="var(--color-primary)" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold font-[var(--font-body)] text-[var(--color-foreground)]">{branch?.name}</p>
                      <p className="text-xs text-[var(--color-muted-foreground)] font-[var(--font-caption)]">{branch?.address || 'No address set'}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="hidden sm:flex items-center gap-3 text-xs font-[var(--font-caption)] text-[var(--color-muted-foreground)]">
                      <span className="flex items-center gap-1"><Icon name="Users" size={12} color="currentColor" />{memberCounts?.[branch?.id] || 0}</span>
                      <span className="flex items-center gap-1"><Icon name="Maximize2" size={12} color="currentColor" />Cap: {branch?.capacity}</span>
                    </div>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium font-[var(--font-caption)] ${
                      branch?.is_active ? 'bg-[var(--color-success)]/10 text-[var(--color-success)]' : 'bg-[var(--color-error)]/10 text-[var(--color-error)]'
                    }`}>{branch?.is_active ? 'Active' : 'Inactive'}</span>
                    <button
                      onClick={() => openEdit(branch)}
                      className="p-1.5 rounded hover:bg-[var(--color-muted)] transition-colors"
                      title="Edit branch"
                    >
                      <Icon name="Pencil" size={14} color="var(--color-muted-foreground)" />
                    </button>
                    <button
                      onClick={() => { setConfirmDeleteId(branch?.id); setDeleteError(null); }}
                      className="p-1.5 rounded hover:bg-[var(--color-error)]/10 transition-colors"
                      title="Delete branch"
                    >
                      <Icon name="Trash2" size={14} color="var(--color-error)" />
                    </button>
                  </div>
                </div>

                {/* Inline delete confirmation */}
                {confirmDeleteId === branch?.id && (
                  <div className="mt-3 px-3 py-2.5 bg-[var(--color-error)]/5 border border-[var(--color-error)]/20 rounded-md space-y-2">
                    {deleteError ? (
                      <div className="flex items-start gap-2">
                        <Icon name="AlertTriangle" size={13} color="var(--color-error)" className="mt-0.5 flex-shrink-0" />
                        <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)]">{deleteError}</p>
                      </div>
                    ) : (
                      <p className="text-xs text-[var(--color-error)] font-[var(--font-caption)]">
                        Delete <span className="font-semibold">{branch?.name}</span>? This cannot be undone.
                      </p>
                    )}
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => { setConfirmDeleteId(null); setDeleteError(null); }}
                        disabled={deleting}
                        className="px-2.5 py-1 text-xs font-medium border border-[var(--color-border)] rounded text-[var(--color-foreground)] hover:bg-[var(--color-muted)] transition-colors disabled:opacity-50"
                      >
                        {deleteError ? 'Close' : 'Cancel'}
                      </button>
                      {!deleteError && (
                        <button
                          onClick={() => handleDelete(branch?.id)}
                          disabled={deleting}
                          className="px-2.5 py-1 text-xs font-medium bg-[var(--color-error)] text-white rounded hover:opacity-90 transition-opacity disabled:opacity-60"
                        >
                          {deleting ? 'Deleting...' : 'Delete'}
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}
